import React from 'react';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/react';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import { ThemeProvider } from '../contexts/ThemeContext';
import FadeIn from '../utils/FadeIn';

// ThemeProvider already hides overflow on <body>; this also pins <html> and
// disables rubber-band scrolling, so there is no way to scroll the takeover
// aside to reveal anything underneath it.
const lockViewport = css`
    html,
    body {
        height: 100%;
        overflow: hidden;
        overscroll-behavior: none;
    }
`;

const StyledMaintenanceNotice = styled.div`
    position: fixed;
    inset: 0;
    /* Deliberately top of the stacking order: nothing the app or a third-party
       script renders may sit above the takeover. */
    z-index: 2147483647;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    padding: 1rem;
    overflow: hidden;
    background-color: var(--body-background-color);
    transition: background-color 0.2s ease-in-out;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    /* Half the viewport rather than half a dashboard panel, so cap the measure
       on wide screens and let it breathe on narrow ones. */
    width: min(90%, 42rem);
    height: auto;
    padding: 1rem;
    border-radius: 25px;
    text-align: center;
    color: var(--kpi-font-color);

    h2 {
        color: var(--kpi-font-color);
        margin-bottom: 0.5rem;
    }

    h4 {
        color: var(--kpi-font-color);
        font-weight: 400;
        margin-top: 0;
    }
`;

/**
 * Full-viewport maintenance takeover, shown in place of the entire portal.
 *
 * Rendered *instead of* the page and its chrome (see `wrapPageElement` in
 * gatsby-browser.js / gatsby-ssr.js) rather than layered on top of it, so the
 * navbar, navigation, KPI row, carousel and embeds are never mounted at all —
 * nothing behind it can be clicked, focused, tabbed to, or scrolled, because
 * there is nothing behind it.
 *
 * It brings its own ThemeProvider because the usual one lives in SiteCore,
 * which is part of the chrome this replaces; the theme's CSS custom properties
 * and the ThemeContext that FadeIn reads both come from there.
 *
 * The copy is deliberately generic — it must not reference vendors,
 * infrastructure, or anything else about why the portal is down.
 */
const MaintenanceScreen = () => (
    <ThemeProvider>
        <Global styles={lockViewport} />
        <StyledMaintenanceNotice role="status" aria-live="polite">
            <Container>
                <FadeIn delay={100}>
                    <BuildRoundedIcon sx={{ fontSize: '4rem' }} />
                    <h2>Our analytics dashboards are temporarily unavailable.</h2>
                    <h4>They're down for scheduled maintenance and will be restored shortly. Thanks for your patience.</h4>
                </FadeIn>
            </Container>
        </StyledMaintenanceNotice>
    </ThemeProvider>
);

export default MaintenanceScreen;
